import { getRulesStore, getReportingStore } from '../storage/stores';
import { EventsReportResult } from '../types';
import { report } from '../api/reportEvents';
import { fetchRules } from '../api/fetchRules';
import { validateDomain } from '../validators/domainValidators';
import { RulesStore } from '../types/store';
import { SiteClassification } from '../types/api/types';
import { WebEvent } from '../types/api/events';
import { updateConsumedQuota } from './deviceUserActions';
import { getTrackingData } from '../chrome/messaging/contentScriptMessaging';
import { TAMPERING_CHECK_POLLING_INTERVAL } from '../constants';
import { isExtensionActive } from '../helper/extensionStatus';
import { sendTamperingEvent } from './accountActions';
import { isProtectionEnabled } from '../protectionStatus';
import { clearVerdictCache, ProcessedVerdict } from '../navigation/verdict';
import { now } from '../lib/dateTime';
import { setupTamperingChecksCancellationListener } from '../tampering';
import { updateTabsStateIfNeeded } from '../chrome/tabManager';
import { captureException } from '../helper/sentry';
import { APIError } from '../lib/errors/api/APIError';
import {
  applyCurrentRulesActionsFromStore,
  onRulesUpdated,
  shouldForceRulesRefreshAfterExtensionUpdate,
  shouldForceRulesRefreshForRoutineAlarm,
} from './rulesActions';
import { getExtensionVersion } from '../chrome/api';

declare const isDelegation: boolean;

export const validateClassification = ({
  url,
  hostname,
  rules,
  classification,
}: {
  url: string;
  hostname: string;
  rules: RulesStore;
  classification?: SiteClassification;
}): ProcessedVerdict => {
  const { categories, categories_match, action, reason, verdict } =
    validateDomain(rules, classification);

  return {
    verdict,
    webEvent: {
      url,
      hostname,
      categories,
      categories_match,
      action,
      reason,
    },
  };
};

export const reportAndUpdate = (event: WebEvent) => {
  const {
    url,
    hostname,
    categories,
    categories_match,
    action,
    reason,
    subtype,
    video_title,
    video_channel_title,
    video_duration,
  } = event;

  return report({
    url,
    hostname,
    categories,
    categories_match,
    action,
    reason,
    ...(subtype !== undefined && { subtype }),
    ...(video_title !== undefined && { video_title }),
    ...(video_channel_title !== undefined && { video_channel_title }),
    ...(video_duration !== undefined && { video_duration }),
  })
    .then(reportResult => updateRulesIfNeeded({ reportResult }))
    .then(reportResult => updateConsumedQuota(reportResult.countableSeconds))
    .then(() => updateReportingTimestamp());
};

const updateReportingTimestamp = async () => {
  const timestamp = await now();
  await getReportingStore().setTimestamp(timestamp);
  console.debug(`Reporting store updated with timestamp=${timestamp}.`);
};

export const syncRules = async ({
  forceRefresh = false,
}: {
  forceRefresh?: boolean;
} = {}) => {
  try {
    const effectiveForceRefresh =
      forceRefresh ||
      shouldForceRulesRefreshAfterExtensionUpdate() ||
      shouldForceRulesRefreshForRoutineAlarm();
    const rules = await fetchRules({ forceRefresh: effectiveForceRefresh });
    const previousRules = getRulesStore().get();
    // Single awaited write so the snapshot and its version persist together
    // and storage failures surface in the catch below.
    const lastSyncExtVersion = getExtensionVersion();
    await getRulesStore().set({
      ...rules,
      lastSyncedExtensionVersion: lastSyncExtVersion,
    });
    console.info(
      `Rules updated timestamp=${rules.timestamp}. Last synced version=${lastSyncExtVersion}`
    );
    // Rules changed: previously cached verdicts (e.g. BLOCK_BY_QUOTA) would
    // otherwise outlive the state transition and keep blocking navigations
    // even after extra time is granted or a pause / routine ends.
    clearVerdictCache();
    // Rule-driven side effects are dispatched through `rulesActions` and run
    // isolated from each other, so a failure in one (e.g. chrome.alarms or
    // chrome.notifications) does not prevent the user-visible tab refresh
    // from running.
    await onRulesUpdated(previousRules, rules).catch(captureException);
    // Once rules are fetched, we might need to refresh some Chrome tabs if they are affected by the rules.
    await updateTabsStateIfNeeded('rules-refresh');
  } catch (error) {
    if (error instanceof APIError && error.status === 304) {
      console.warn('Rules were not modified (304)');
      await applyCurrentRulesActionsFromStore();
    } else {
      console.error('SyncRules error:', error);
      captureException(error as Error);
    }
  }
};

const updateRulesIfNeeded = ({
  reportResult,
}: {
  reportResult: EventsReportResult;
}) => {
  if (reportResult.devicePolicyTimestamp !== getRulesStore().getTimestamp()) {
    syncRules();
  }

  return reportResult;
};

export const shouldReportTampering = () =>
  !isDelegation && isProtectionEnabled();

export const startCheckIfActivePolling = async () => {
  // Device UID needs to be fetched as soon as possible.
  // Once the extension gets disabled or removed the store is no longer available.
  const trackingData = await getTrackingData();
  if (!trackingData) {
    // Extension still not configured
    return;
  }

  const { deviceUid, accountUid, profileId, profileName } = trackingData;
  const pollingRef = setInterval(() => {
    if (isExtensionActive() || !isProtectionEnabled()) {
      return;
    }

    sendTamperingEvent({ deviceUid, accountUid, profileId, profileName });
    clearInterval(pollingRef);
  }, TAMPERING_CHECK_POLLING_INTERVAL);

  // Cancel tampering checks when requested from the service worker
  setupTamperingChecksCancellationListener(() => clearInterval(pollingRef));
};
